import * as React from "react";
import styled from "styled-components";
import { TextInput, SelectMenu, SelectMenuItem } from "../../elements";
import Tag from "../Tag";
import composeRefs from "../../util/composeRefs";
import Downshift, { DownshiftState, StateChangeOptions } from "downshift";
import MenuLayer from "../../components/MenuLayer";
import posed, { PoseGroup } from "react-pose";
import { useSelect } from "@hubper/theme";
import { mergeStyles, highlightText } from "../../util";

type Tag = {
  id: string;
  name: string;
};

const Base = styled.div`
  display: inline-block;
  position: relative;
`;

const TagContainer = styled.div``;

const AnimatedTag = posed(Tag)({
  enter: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 40
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 40
    }
  }
});

const filteredTags = (
  tags: Tag[],
  activeTags: Tag[],
  inputValue: string | null
) => {
  if (!inputValue) {
    return tags;
  }
  return tags
    .filter(x => !activeTags.some(y => y.id === x.id))
    .filter(
      tag =>
        !inputValue || tag.name.toLowerCase().includes(inputValue.toLowerCase())
    );
};

const stateReducer = (
  state: DownshiftState<Tag>,
  changes: StateChangeOptions<Tag>
) => {
  switch (changes.type) {
    case Downshift.stateChangeTypes.changeInput:
      return {
        ...changes,
        highlightedIndex: 0
      };
    case Downshift.stateChangeTypes.keyDownEnter:
    case Downshift.stateChangeTypes.clickItem:
      return {
        ...changes,
        isOpen: false,
        highlightedIndex: state.highlightedIndex,
        inputValue: ""
      };
    default:
      return changes;
  }
};

type Props = {
  style?: React.CSSProperties;
  className?: string;
  label?: string;
  placeholder?: string;
  value: Tag[];
  disabled?: boolean;
  stretch?: boolean;
  tags: Tag[];
  addNewTag?: boolean;
  addText?: string;
  onChange: (value: Tag[]) => void;
  onSearchChange?: (searchStr: string) => void;
  onAdd?: (tag: Tag) => void;
};

/**
 * - Als ik type "Apple" denk werkt autocomplete niet. 'apple' wel.
 * - Mooi om karakters in zoektext bold te maken (zie util/highlightText)
 * - Als er geen items zijn gevonden -> geen menu tonen
 * - Het moet mogelijk zijn om ook een niet bestaande tag toe te voegen. Dwz:
 *   Als ik 'Blueberry' type, en op 'enter' druk, moet er ook een tag toegevoegd worden
 * - Voor het toevoegen van nieuwe items en het zoeken naar bestaasnde moeten we even kijken
 *   Hoe we daar met de API mee omgaan...
 */
function TagSearchInput(
  {
    style,
    className,
    tags,
    label,
    value = [],
    onChange,
    stretch,
    disabled,
    placeholder,
    addNewTag,
    onSearchChange,
    addText = "add",
    onAdd
  }: Props,
  ref: any
) {
  const inputRef = React.useRef<HTMLDivElement>(null);
  const { color, space } = useSelect();

  const addTag = (tag: Tag) => {
    if (value.indexOf(tag) === -1) {
      onChange([...value, tag]);
    }
  };

  const deleteTag = (tagId: string) => {
    onChange(value.filter(tag => tag.id !== tagId));
  };

  const handleSelection = (tag: Tag) => {
    if (value.includes(tag)) {
      deleteTag(tag.id);
    } else {
      if (tag.id === "new" && onAdd) {
        onAdd!(tag);
      } else {
        addTag(tag);
      }
    }
  };

  return (
    <Downshift
      onChange={handleSelection}
      onInputValueChange={onSearchChange}
      itemToString={item => (item ? item.id : "")}
      stateReducer={stateReducer}
      selectedItem={null}
    >
      {({
        isOpen,
        getRootProps,
        getMenuProps,
        getItemProps,
        getInputProps,
        inputValue = "",
        highlightedIndex
      }) => {
        const tagExists = tags.some(tag =>
          inputValue
            ? inputValue.toLowerCase() === tag.name.toLowerCase()
            : false
        );

        const filteredItems = filteredTags(tags, value, inputValue);
        const menuIsVisible =
          (filteredItems.length > 0 || addNewTag) && inputValue !== "";

        return (
          <Base
            {...getRootProps()}
            style={mergeStyles({ width: stretch ? "100%" : 250 }, style)}
            className={className}
          >
            <TextInput
              containerRef={composeRefs(ref, inputRef)}
              label={label}
              placeholder={placeholder}
              stretch={true}
              disabled={disabled}
              style={{
                transition: "0.2s",
                borderBottomLeftRadius: menuIsVisible ? 0 : 3,
                borderBottomRightRadius: menuIsVisible ? 0 : 3,
                borderBottomColor: menuIsVisible
                  ? color(["grey", 1])
                  : undefined
              }}
              {...getInputProps()}
            />
            {menuIsVisible && (
              <MenuLayer element={inputRef.current}>
                {style => (
                  <PoseGroup flipMove={false}>
                    {isOpen && (
                      <SelectMenu key="menu" {...getMenuProps({ style })}>
                        {filteredItems.map((tag, index) => (
                          <SelectMenuItem
                            key={tag.id}
                            highlighted={highlightedIndex === index}
                            {...getItemProps({
                              item: tag,
                              index
                            })}
                          >
                            {highlightText(
                              tag.name as string,
                              inputValue as string
                            )}
                          </SelectMenuItem>
                        ))}
                        {addNewTag && inputValue && !tagExists && (
                          <SelectMenuItem
                            key={inputValue}
                            highlighted={
                              highlightedIndex === filteredItems.length
                            }
                            {...getItemProps({
                              item: {
                                id: "new",
                                name: inputValue
                              }
                            })}
                          >
                            {`${addText}: ${inputValue}`}
                          </SelectMenuItem>
                        )}
                      </SelectMenu>
                    )}
                  </PoseGroup>
                )}
              </MenuLayer>
            )}
            <TagContainer
              style={{ marginTop: value.length > 0 ? space(1) : "0px" }}
            >
              <PoseGroup>
                {value.map((tag: Tag) => (
                  <AnimatedTag
                    text={tag.name}
                    key={tag.id}
                    onDelete={() => {
                      deleteTag(tag.id);
                      inputValue = "";
                    }}
                  />
                ))}
              </PoseGroup>
            </TagContainer>
          </Base>
        );
      }}
    </Downshift>
  );
}

export default React.forwardRef(TagSearchInput);
